import React, { useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Text,
  TextInput,
} from "@mantine/core";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import {
  getAddressByPlaceId,
  searchAddress,
} from "../../services/dashboard";

const emptyEntry = { label: "", address: "", city: "", state: "", zipCode: "" };

// ─── Inline add/edit form ────────────────────────────────────────────────────
const LocationForm = ({
  formData,
  setFormData,
  suggestions,
  showSuggestions,
  setShowSuggestions,
  addrLoading,
  onAddressSearch,
  onSelectAddress,
  onSave,
  onCancel,
}) => (
  <Box>
    <TextInput
      label="Label"
      placeholder="e.g. Home, School, Day Program, Work"
      size="xs"
      mb={8}
      required
      value={formData.label}
      onChange={(e) =>
        setFormData((prev) => ({ ...prev, label: e.target.value }))
      }
    />

    {/* Address with Google Places autocomplete */}
    <Box style={{ position: "relative" }} mb={8}>
      <TextInput
        label="Address"
        placeholder="Start typing an address..."
        size="xs"
        required
        value={formData.address}
        onChange={(e) => onAddressSearch(e.target.value)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        rightSection={addrLoading ? <Loader size="xs" /> : null}
      />
      {showSuggestions && suggestions.length > 0 && (
        <Box
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 300,
            background: "var(--app-surface)",
            border: "1px solid var(--app-border)",
            borderRadius: 8,
            maxHeight: 180,
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {suggestions.map((s, i) => (
            <Box
              key={s.place_id || i}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              sx={{ "&:hover": { background: "var(--app-surface-2)" } }}
              onMouseDown={() => onSelectAddress(s)}
            >
              {s.formatted_address}
            </Box>
          ))}
        </Box>
      )}
    </Box>

    <Flex gap={8} mb={10}>
      <TextInput
        label="City"
        placeholder="City"
        size="xs"
        style={{ flex: 1 }}
        value={formData.city}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, city: e.target.value }))
        }
      />
      <TextInput
        label="State"
        placeholder="State"
        size="xs"
        style={{ flex: 1 }}
        value={formData.state}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, state: e.target.value }))
        }
      />
      <TextInput
        label="Zip Code"
        placeholder="Zip"
        size="xs"
        style={{ flex: 1 }}
        value={formData.zipCode}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, zipCode: e.target.value }))
        }
      />
    </Flex>

    <Group position="right" spacing={8}>
      <Button size="xs" variant="subtle" color="gray" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        size="xs"
        color="yellow"
        c="var(--app-on-primary)"
        onClick={onSave}
        disabled={!formData.label.trim() || !formData.address.trim()}
      >
        Save Location
      </Button>
    </Group>
  </Box>
);

// ─── Main component ──────────────────────────────────────────────────────────
const PreferredLocationsList = ({
  title,
  locations = [],
  onChange,
  disabled,
}) => {
  const [adding, setAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({ ...emptyEntry });

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const debounceRef = useRef(null);

  const isFormOpen = adding || editingIndex !== null;

  // ── Address search ──
  const handleAddressSearch = (value) => {
    setFormData((prev) => ({ ...prev, address: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const results = await searchAddress(value);
        setSuggestions(results || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
      setAddrLoading(false);
    }, 400);
  };

  const handleSelectAddress = async (suggestion) => {
    setShowSuggestions(false);
    setAddrLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components = placeDetails?.result?.address_components || [];
      let streetNumber = "",
        route = "",
        city = "",
        addrState = "",
        zipcode = "";
      for (const comp of components) {
        if (comp.types.includes("street_number"))
          streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1"))
          addrState = comp.long_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      }
      setFormData((prev) => ({
        ...prev,
        address:
          [streetNumber, route].filter(Boolean).join(" ") ||
          suggestion.formatted_address,
        city: city || prev.city,
        state: addrState || prev.state,
        zipCode: zipcode || prev.zipCode,
      }));
    } catch {
      setFormData((prev) => ({
        ...prev,
        address: suggestion.formatted_address,
      }));
    }
    setAddrLoading(false);
  };

  // ── CRUD ──
  const handleSave = () => {
    if (!formData.label.trim() || !formData.address.trim()) return;
    const updated = [...locations];
    if (editingIndex !== null) {
      updated[editingIndex] = { ...updated[editingIndex], ...formData };
    } else {
      updated.push({ ...formData });
    }
    onChange(updated);
    resetForm();
  };

  const handleEdit = (index) => {
    setFormData({ ...emptyEntry, ...locations[index] });
    setEditingIndex(index);
    setAdding(false);
    setSuggestions([]);
  };

  const handleDelete = (index) => {
    onChange(locations.filter((_, i) => i !== index));
    if (editingIndex === index) resetForm();
  };

  const resetForm = () => {
    setAdding(false);
    setEditingIndex(null);
    setFormData({ ...emptyEntry });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const formProps = {
    formData,
    setFormData,
    suggestions,
    showSuggestions,
    setShowSuggestions,
    addrLoading,
    onAddressSearch: handleAddressSearch,
    onSelectAddress: handleSelectAddress,
    onSave: handleSave,
    onCancel: resetForm,
  };

  return (
    <Box>
      {/* ── Header ── */}
      <Flex align="center" justify="space-between" mb={8}>
        <Text fw={600} fz={13} color="var(--app-text)">
          {title}
        </Text>
        {!disabled && (
          <Button
            size="xs"
            variant="light"
            color="yellow"
            c="var(--app-on-primary)"
            leftIcon={<IconPlus size={12} />}
            onClick={() => {
              setAdding(true);
              setEditingIndex(null);
              setFormData({ ...emptyEntry });
              setSuggestions([]);
            }}
            disabled={isFormOpen}
          >
            Add
          </Button>
        )}
      </Flex>

      {/* ── Empty state ── */}
      {locations.length === 0 && !isFormOpen && (
        <Text fz={12} color="dimmed" mb={6}>
          No locations added yet.
        </Text>
      )}

      {/* ── Saved locations ── */}
      {locations.map((loc, index) => (
        <Box
          key={index}
          mb={6}
          px={12}
          py={8}
          style={{
            background: "var(--app-surface-2)",
            borderRadius: 8,
            border: "1px solid var(--app-border)",
          }}
        >
          {editingIndex === index ? (
            <LocationForm {...formProps} />
          ) : (
            <Flex align="center" justify="space-between" gap={8}>
              <Flex align="center" gap={8} style={{ flexWrap: "wrap", flex: 1 }}>
                <Badge size="xs" color="yellow" variant="light">
                  {loc.label}
                </Badge>
                <Text fz={12} color="var(--app-text)">
                  {[loc.address, loc.city, loc.state, loc.zipCode]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
              </Flex>
              {!disabled && (
                <Group spacing={4} style={{ flexShrink: 0 }}>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="blue"
                    onClick={() => handleEdit(index)}
                    disabled={isFormOpen}
                  >
                    <IconEdit size={13} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => handleDelete(index)}
                    disabled={isFormOpen}
                  >
                    <IconTrash size={13} />
                  </ActionIcon>
                </Group>
              )}
            </Flex>
          )}
        </Box>
      ))}

      {/* ── Add new form ── */}
      {adding && (
        <Box
          px={12}
          py={10}
          style={{
            background: "var(--app-tint-amber)",
            borderRadius: 8,
            border: "1px dashed var(--app-primary)",
          }}
        >
          <LocationForm {...formProps} />
        </Box>
      )}
    </Box>
  );
};

export default PreferredLocationsList;
